import { UseQueryOptions, useMutation, useQuery } from 'react-query';

import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

import { postReasons } from '@api/common';
import {
  disconnectSocial,
  fetchFanClub,
  fetchMyFanClub,
  fetchSocialProviders,
  fetchUser,
  fetchUserList,
  postEmailCrendentialToUser,
  postMyEmailVerification,
  postSocial,
  unregisterUser,
} from '@api/user';
import { QUERY_KEYS, UniversalReason } from '@constants';
import { IUserDetail } from '@model';

export const useUserQuery = () => {
  const useFetchUser = (
    userId: number | undefined,
    options?: UseQueryOptions<IUserDetail>
  ) => {
    return useQuery<IUserDetail>(
      QUERY_KEYS.USER.ONE(userId),
      () => fetchUser(userId),
      {
        staleTime: minutesToMilliseconds(15),
        cacheTime: minutesToMilliseconds(15),
        enabled: !!userId,
        ...options,
      }
    );
  };

  const useFetchUserList = (
    userIds: number[],
    options?: UseQueryOptions<IUserDetail[]>
  ) => {
    return useQuery<IUserDetail[]>(
      QUERY_KEYS.USER.LIST(userIds),
      () => fetchUserList(userIds),
      {
        staleTime: minutesToMilliseconds(15),
        cacheTime: minutesToMilliseconds(15),
        enabled: !!userIds?.length,
        ...options,
      }
    );
  };

  const useFetchFanClub = (userId: number) => {
    const query = useQuery(
      QUERY_KEYS.FAN.INFO(userId),
      () => fetchFanClub(userId),
      {
        staleTime: minutesToMilliseconds(5),
        cacheTime: minutesToMilliseconds(5),
      }
    );

    return query;
  };

  //me로 옮길 쿼리
  const useFetchMyFanClub = () => {
    const query = useQuery(
      QUERY_KEYS.MY_FAN_CLUB.INFO,
      () => fetchMyFanClub(),
      {
        staleTime: minutesToMilliseconds(5),
        cacheTime: minutesToMilliseconds(5),
      }
    );

    return query;
  };

  const useFetchSocialProviders = () => {
    const query = useQuery(QUERY_KEYS.USER.SOCIAL_PROVIDER, () =>
      fetchSocialProviders()
    );

    return query;
  };

  const { mutateAsync: postEmailCredential } = useMutation(
    async (email: string) => await postEmailCrendentialToUser(email)
  );

  const { mutate: postEmailVerification } = useMutation(
    async () => await postMyEmailVerification()
  );
  const { mutateAsync: postSocialPeristalsis } = useMutation(
    async ({
      social,
      accessToken,
      idToken,
    }: {
      social: string;
      accessToken: string;
      idToken: string | undefined;
    }) => await postSocial(social, accessToken, idToken)
  );

  const { mutateAsync: disconnectSocialPeristalsis } = useMutation(
    async ({ social }: { social: string }) => await disconnectSocial(social)
  );

  const { mutateAsync: deleteUser } = useMutation(
    async () => await unregisterUser()
  );

  const { mutateAsync: postUnregisterReasons } = useMutation(
    (reason: string[]) =>
      postReasons(UniversalReason.unregisterUser, { reason })
  );

  return {
    disconnectSocialPeristalsis,
    useFetchUser,
    useFetchUserList,
    useFetchFanClub,
    useFetchMyFanClub,
    postEmailCredential,
    postEmailVerification,
    useFetchSocialProviders,
    postSocialPeristalsis,
    deleteUser,
    postUnregisterReasons,
  };
};
